import {
  ContentControlledEditingDirectiveViewModel,
} from 'app/js/src/domain/content-control/editing-directive';
import { ContentControlledEventActionEnum } from 'app/js/src/domain/content-control/enums';
import { HlsStreamVideoSource } from 'app/js/src/domain/content-control/hls-stream-video-source';

/**
 * Cached cut segment in Timeline Seconds (TS) domain.
 * Pre-computed from UTC millisecond segments to avoid repeated conversions.
 */
export class CutSegmentTimelineSeconds {
  constructor(
    public readonly startTimelineSeconds: number,
    public readonly endTimelineSeconds: number,
  ) {}

  public get durationSecs(): number {
    return this.endTimelineSeconds - this.startTimelineSeconds;
  }
}

/**
 * Unified coordinate system for the live timeline.
 *
 * Defines **Timeline Seconds (TS)** — a zero-based seconds scale relative to
 * the HLS playlist start: `ts = (utcMs - originMs) / 1000`, where `originMs`
 * is the stable `playlistStartTimestampMs` captured once at manifest parse.
 *
 * In other words, TS represents "seconds since the stream started". For example,
 * if the playlist started at UTC 1712678400000 and a segment has UTC 1712678460000,
 * its TS value is 60 — meaning 60 seconds after the stream began.
 *
 * This abstraction exists because working with raw UTC milliseconds everywhere is
 * error-prone and hard to reason about. TS provides a single, stable reference
 * frame that decouples all timeline logic from raw UTC timestamps and
 * player-specific time offsets.
 *
 * ALL internal timeline math (viewport, zoom, segments, playhead, ET/OT mapping)
 * uses TS. Conversions happen only at two boundaries:
 * - **Input**: UTC ms from segments/HLS → TS
 * - **Output**: TS → pixels (rendering) or TS → player-relative seconds (seeking)
 */
export class LiveTimelineCoordinateSystem {

  // ---------------------------------------------------------------------------
  // Origin (set once at initialization)
  // ---------------------------------------------------------------------------

  /** Stable UTC origin (ms) for the TS coordinate space. */
  private _originMs = 0;

  // ---------------------------------------------------------------------------
  // Viewport state
  // ---------------------------------------------------------------------------

  /** Left edge of the visible area in TS. */
  private _viewportStartTimelineSeconds = 0;

  /** Duration of the visible area in seconds. */
  private _viewportDurationSecs = 1;

  /** Container width in pixels. */
  private _containerWidthPx = 1;

  /** Whether the viewport should auto-scroll with the live edge. */
  private _isFollowingLive = true;

  // ---------------------------------------------------------------------------
  // ET (Edited Time) state
  // ---------------------------------------------------------------------------

  /** Whether ET mode is active. */
  private _isEtMode = false;

  /** Sorted list of cut segments in TS domain. */
  private _cutSegmentsTimelineSeconds: CutSegmentTimelineSeconds[] = [];

  /** Duration of a single frame in seconds, used for ET gap adjustment. */
  private _oneFrameSecs = 0;

  // ---------------------------------------------------------------------------
  // Dependencies
  // ---------------------------------------------------------------------------

  /** HLS video source for player-relative <-> UTC conversions. */
  private _hlsSource!: HlsStreamVideoSource;

  // ---------------------------------------------------------------------------
  // Constructor & initialization
  // ---------------------------------------------------------------------------

  /**
   * Initializes the coordinate system with the stable playlist origin.
   * @param originMs - `playlistStartTimestampMs` from the HLS source, captured once.
   * @param hlsSource - The HLS video source for player-relative time conversions.
   * @param oneFrameSecs - Duration of a single frame in seconds.
   */
  public initialize(originMs: number, hlsSource: HlsStreamVideoSource, oneFrameSecs: number): void {
    this._originMs = originMs;
    this._hlsSource = hlsSource;
    this._oneFrameSecs = oneFrameSecs;
  }

  // ---------------------------------------------------------------------------
  // Coordinate conversions (pure, no side effects)
  // ---------------------------------------------------------------------------

  /** Converts a UTC timestamp (ms) to Timeline Seconds. */
  public utcMsToTimelineSeconds(utcMs: number): number {
    return (utcMs - this._originMs) / 1000;
  }

  /** Converts Timeline Seconds to a UTC timestamp (ms). */
  public timelineSecondsToUtcMs(timelineSeconds: number): number {
    return timelineSeconds * 1000 + this._originMs;
  }

  /**
   * Converts Timeline Seconds to player-relative seconds.
   * Delegates to the HLS source's `timestampUtcToPlayerRelativeTimeInSeconds`.
   */
  public timelineSecondsToPlayerSecs(timelineSeconds: number): number {
    return this._hlsSource.timestampUtcToPlayerRelativeTimeInSeconds(this.timelineSecondsToUtcMs(timelineSeconds));
  }

  /**
   * Converts player-relative seconds to Timeline Seconds.
   * Delegates to the HLS source's `convertPlayerRelativeTimeToUtcTimestampMs`.
   */
  public playerSecsToTimelineSeconds(playerSecs: number): number {
    const utcMs = this._hlsSource.convertPlayerRelativeTimeToUtcTimestampMs(playerSecs);
    return this.utcMsToTimelineSeconds(utcMs);
  }

  // ---------------------------------------------------------------------------
  // OT/ET mapping
  // ---------------------------------------------------------------------------

  /**
   * Maps Original Time TS to Edited Time seconds by subtracting the
   * duration of all cut segments that start before the given position.
   * When ET mode is off, returns the input unchanged.
   */
  public otToEt(otTimelineSeconds: number): number {
    if (!this._isEtMode || this._cutSegmentsTimelineSeconds.length === 0) return otTimelineSeconds;
    let totalCutSecs = 0;
    for (const cut of this._cutSegmentsTimelineSeconds) {
      if (cut.startTimelineSeconds >= otTimelineSeconds) break;
      const overlapEnd = Math.min(cut.endTimelineSeconds, otTimelineSeconds);
      totalCutSecs += overlapEnd - cut.startTimelineSeconds;
    }
    return otTimelineSeconds - totalCutSecs;
  }

  /**
   * Maps Edited Time seconds back to Original Time TS by expanding
   * cut gaps. When ET mode is off, returns the input unchanged.
   */
  public etToOt(etTimelineSeconds: number): number {
    if (!this._isEtMode || this._cutSegmentsTimelineSeconds.length === 0) return etTimelineSeconds;
    let accumulated = 0;
    let otCandidate = etTimelineSeconds;
    for (const cut of this._cutSegmentsTimelineSeconds) {
      if (cut.startTimelineSeconds >= otCandidate) break;
      accumulated += cut.durationSecs;
      otCandidate = etTimelineSeconds + accumulated;
    }
    return otCandidate;
  }

  // ---------------------------------------------------------------------------
  // Pixel conversions (viewport-aware)
  // ---------------------------------------------------------------------------

  /** Current pixel density: pixels per second of visible timeline. */
  public get pixelsPerSecond(): number {
    return this._viewportDurationSecs > 0
      ? this._containerWidthPx / this._viewportDurationSecs
      : 0;
  }

  /**
   * Converts Timeline Seconds to a pixel offset relative to the viewport.
   * Applies ET mapping if active, then subtracts the viewport start.
   */
  public timelineSecondsToPixel(timelineSeconds: number): number {
    const effectiveTimelineSeconds = this._isEtMode ? this.otToEt(timelineSeconds) : timelineSeconds;
    return (effectiveTimelineSeconds - this._viewportStartEt) * this.pixelsPerSecond;
  }

  /**
   * Converts a pixel offset (relative to viewport) to Timeline Seconds.
   * Undoes ET mapping if active.
   */
  public pixelToTimelineSeconds(px: number): number {
    const pps = this.pixelsPerSecond;
    if (pps <= 0) return 0;
    const etTimelineSeconds = this._viewportStartEt + px / pps;
    return this._isEtMode ? this.etToOt(etTimelineSeconds) : etTimelineSeconds;
  }

  /**
   * Converts a pixel offset (relative to viewport) to a UTC timestamp (ms).
   * Convenience for tooltip display.
   */
  public pixelToUtcMs(px: number): number {
    return this.timelineSecondsToUtcMs(this.pixelToTimelineSeconds(px));
  }

  // ---------------------------------------------------------------------------
  // Viewport management
  // ---------------------------------------------------------------------------

  public get viewportStartTimelineSeconds(): number { return this._viewportStartTimelineSeconds; }
  public get viewportDurationSecs(): number { return this._viewportDurationSecs; }
  public get containerWidthPx(): number { return this._containerWidthPx; }
  public get isFollowingLive(): boolean { return this._isFollowingLive; }
  public get originMs(): number { return this._originMs; }

  /**
   * Sets the viewport to a specific range and container width.
   * This is the primary way to update the visible area on zoom or resize.
   */
  public setViewport(startTimelineSeconds: number, durationSecs: number, containerWidthPx: number): void {
    this._viewportStartTimelineSeconds = startTimelineSeconds;
    this._viewportDurationSecs = Math.max(durationSecs, 0.001);
    this._containerWidthPx = Math.max(containerWidthPx, 1);
  }

  /**
   * Configures the viewport to fit a segment with margins.
   * @param segStartTimelineSeconds - Segment start in Timeline Seconds.
   * @param segEndTimelineSeconds - Segment end in Timeline Seconds.
   * @param marginDivisor - Fraction of segment duration to use as margin on each side.
   * @param containerWidthPx - Container width in pixels.
   * @param maxTimelineSeconds - Maximum Timeline Seconds (playlist end), used to clamp end margin.
   */
  public setFitSegmentViewport(
    segStartTimelineSeconds: number,
    segEndTimelineSeconds: number,
    marginDivisor: number,
    containerWidthPx: number,
    maxTimelineSeconds: number,
  ): void {
    const segDuration = segEndTimelineSeconds - segStartTimelineSeconds;
    const marginSecs = segDuration / marginDivisor;
    const marginStart = Math.min(marginSecs, segStartTimelineSeconds);
    const remainingToEnd = maxTimelineSeconds - segEndTimelineSeconds;
    const marginEnd = Math.min(marginSecs, Math.max(remainingToEnd, 0));
    const startTimelineSeconds = segStartTimelineSeconds - marginStart;
    const duration = segDuration + marginStart + marginEnd;
    this.setViewport(startTimelineSeconds, duration, containerWidthPx);
    this._isFollowingLive = false;
  }

  /**
   * Slides the viewport to keep the playhead at approximately 85% from the left.
   * Only takes effect when `isFollowingLive` is true.
   * @param currentTimelineSeconds - Current playhead position in Timeline Seconds.
   */
  public followLiveEdge(currentTimelineSeconds: number): void {
    if (!this._isFollowingLive) return;
    const playheadRatio = 0.85;
    this._viewportStartTimelineSeconds = currentTimelineSeconds - this._viewportDurationSecs * playheadRatio;
  }

  /** Enables or disables live-edge following. */
  public setFollowingLive(follow: boolean): void {
    this._isFollowingLive = follow;
  }

  // ---------------------------------------------------------------------------
  // ET / cut segment management
  // ---------------------------------------------------------------------------

  /** Updates ET mode flag. */
  public setEtMode(enabled: boolean): void {
    this._isEtMode = enabled;
  }

  public get isEtMode(): boolean { return this._isEtMode; }

  /**
   * Rebuilds the cut segments cache from editing directives.
   * Converts UTC ms to TS and sorts by start position.
   * @param segments - All editing directives (filters to Cut action internally).
   */
  public updateCutSegments(segments: ContentControlledEditingDirectiveViewModel[]): void {
    this._cutSegmentsTimelineSeconds = segments
      .filter(s => s.action === ContentControlledEventActionEnum.Cut)
      .map(s => new CutSegmentTimelineSeconds(this.utcMsToTimelineSeconds(s.startsAtMs), this.utcMsToTimelineSeconds(s.endsAtMs)))
      .sort((a, b) => a.startTimelineSeconds - b.startTimelineSeconds);
  }

  /** Returns the total duration of all cut segments in seconds. */
  public get totalCutDurationSecs(): number {
    return this._cutSegmentsTimelineSeconds.reduce((sum, cut) => sum + cut.durationSecs, 0);
  }

  /** Returns the number of cut segments. */
  public get cutSegmentCount(): number {
    return this._cutSegmentsTimelineSeconds.length;
  }

  // ---------------------------------------------------------------------------
  // Derived getters
  // ---------------------------------------------------------------------------

  /** The viewport start in ET coordinates (for pixel calculations). */
  private get _viewportStartEt(): number {
    return this._isEtMode ? this.otToEt(this._viewportStartTimelineSeconds) : this._viewportStartTimelineSeconds;
  }

  /**
   * Total visible duration in ET mode: viewport duration minus cut segments within the viewport.
   * In OT mode, returns the viewport duration unchanged.
   */
  public get etViewportDurationSecs(): number {
    if (!this._isEtMode) return this._viewportDurationSecs;
    const viewportEndTimelineSeconds = this._viewportStartTimelineSeconds + this._viewportDurationSecs;
    return this._viewportDurationSecs - this._cutDurationInRange(this._viewportStartTimelineSeconds, viewportEndTimelineSeconds);
  }

  /**
   * Returns the total cut duration within a given TS range.
   */
  private _cutDurationInRange(startTimelineSeconds: number, endTimelineSeconds: number): number {
    let total = 0;
    for (const cut of this._cutSegmentsTimelineSeconds) {
      if (cut.endTimelineSeconds <= startTimelineSeconds) continue;
      if (cut.startTimelineSeconds >= endTimelineSeconds) break;
      const overlapStart = Math.max(cut.startTimelineSeconds, startTimelineSeconds);
      const overlapEnd = Math.min(cut.endTimelineSeconds, endTimelineSeconds);
      total += overlapEnd - overlapStart;
    }
    return total;
  }
}
